// Audio + browser notifications for new chats / emails.
// Sound is synthesized via Web Audio (no asset to host).

const PREF_KEY = 'homeiqa_notify_prefs';
const DEFAULT_PREFS = {
  chatSound: true,
  emailSound: true,
  smsSound: true,
  browserNotify: true
};

export function getNotifyPrefs() {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch { return { ...DEFAULT_PREFS }; }
}

export function setNotifyPrefs(next) {
  const merged = { ...getNotifyPrefs(), ...next };
  try { localStorage.setItem(PREF_KEY, JSON.stringify(merged)); } catch {}
  try { window.dispatchEvent(new CustomEvent('homeiqa-notify-prefs', { detail: merged })); } catch {}
  return merged;
}

let audioCtx = null;
function ctx() {
  if (audioCtx) return audioCtx;
  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  } catch { audioCtx = null; }
  return audioCtx;
}

// Attention chime — SMS is louder and uses a three-tone iMessage-style
// pattern so it stands out from chat/email. kind = 'chat'|'email'|'sms'|null.
export function playChime(kind = null, volume) {
  const prefs = getNotifyPrefs();
  if (kind === 'chat' && !prefs.chatSound) return;
  if (kind === 'email' && !prefs.emailSound) return;
  if (kind === 'sms' && !prefs.smsSound) return;
  const ac = ctx();
  if (!ac) return;
  if (ac.state === 'suspended') { ac.resume().catch(() => {}); }

  const vol = volume != null ? volume : (kind === 'sms' ? 0.55 : 0.25);
  const now = ac.currentTime;
  const tone = (freq, start, dur, wave = 'sine') => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = wave;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, now + start);
    gain.gain.linearRampToValueAtTime(vol, now + start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(gain).connect(ac.destination);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.02);
  };

  if (kind === 'sms') {
    // Bright triple-tone alert — quick, unmistakable, roughly like an iMessage ping.
    tone(1046, 0.00, 0.16, 'triangle'); // C6
    tone(1318, 0.14, 0.18, 'triangle'); // E6
    tone(1568, 0.30, 0.34, 'triangle'); // G6
  } else {
    // Softer two-tone for chat / email.
    tone(880, 0.00, 0.18);
    tone(660, 0.18, 0.32);
  }
}

let permissionAsked = false;
export async function ensureBrowserNotifyPermission() {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  if (permissionAsked) return false;
  permissionAsked = true;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch { return false; }
}

export function showBrowserNotification(title, body, onClick) {
  const prefs = getNotifyPrefs();
  if (!prefs.browserNotify) return;
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  // Don't show if the tab is currently visible — sound is enough.
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      tag: 'homeiqa'
    });
    if (onClick) {
      n.onclick = () => {
        try { window.focus(); } catch {}
        try { onClick(); } catch {}
        n.close();
      };
    }
  } catch {}
}
